import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Percent, IndianRupee, Calculator, TrendingUp, Loader2, Save, Trash2, Users } from 'lucide-react';

const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);

export default function AdminFeeManagementPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [merchants, setMerchants] = useState([]);

  // Global form
  const [form, setForm] = useState({ percentage_fee: '', fixed_fee: '', tax_enabled: true, tax_rate: '', enabled: true, effective_from: '' });
  const [saving, setSaving] = useState(false);

  // Preview calculator
  const [previewAmt, setPreviewAmt] = useState('10000');
  const [preview, setPreview] = useState(null);

  // Merchant override
  const [ovMerchant, setOvMerchant] = useState('');
  const [ovForm, setOvForm] = useState({ percentage_fee: '', fixed_fee: '', tax_enabled: false, tax_rate: '', enabled: true });
  const [ovSaving, setOvSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, r, m] = await Promise.all([
        api.get('/admin/fees'),
        api.get('/admin/fees/revenue'),
        api.get('/merchants/'),
      ]);
      setConfig(c.data);
      setRevenue(r.data);
      setMerchants(m.data || []);
      const g = c.data.payout_global;
      if (g) setForm({
        percentage_fee: String(g.percentage_fee ?? ''), fixed_fee: String(g.fixed_fee ?? ''),
        tax_enabled: !!g.tax_enabled, tax_rate: String(g.tax_rate ?? ''), enabled: !!g.enabled, effective_from: '',
      });
    } catch (e) {
      toast.error('Failed to load fee configuration');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const runPreview = useCallback(async () => {
    const amt = parseFloat(previewAmt);
    if (!amt || amt <= 0) { setPreview(null); return; }
    try {
      const res = await api.post('/admin/fees/preview', { amount: amt, service_type: 'payout' });
      setPreview(res.data);
    } catch { setPreview(null); }
  }, [api, previewAmt]);

  useEffect(() => { const t = setTimeout(runPreview, 400); return () => clearTimeout(t); }, [runPreview]);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await api.put('/admin/fees', {
        service_type: 'payout', scope: 'global',
        percentage_fee: parseFloat(form.percentage_fee) || 0,
        fixed_fee: parseFloat(form.fixed_fee) || 0,
        tax_enabled: form.tax_enabled,
        tax_rate: form.tax_enabled ? (parseFloat(form.tax_rate) || 0) : 0,
        enabled: form.enabled,
        effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : null,
      });
      toast.success('Global payout fee updated');
      await load();
      runPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const saveOverride = async () => {
    if (!ovMerchant) { toast.error('Select a merchant'); return; }
    setOvSaving(true);
    try {
      await api.put('/admin/fees', {
        service_type: 'payout', scope: 'merchant', merchant_id: ovMerchant,
        percentage_fee: parseFloat(ovForm.percentage_fee) || 0,
        fixed_fee: parseFloat(ovForm.fixed_fee) || 0,
        tax_enabled: ovForm.tax_enabled,
        tax_rate: ovForm.tax_enabled ? (parseFloat(ovForm.tax_rate) || 0) : 0,
        enabled: ovForm.enabled,
      });
      toast.success('Merchant override saved');
      setOvMerchant(''); setOvForm({ percentage_fee: '', fixed_fee: '', tax_enabled: false, tax_rate: '', enabled: true });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save override');
    } finally { setOvSaving(false); }
  };

  const removeOverride = async (mid) => {
    try {
      await api.delete(`/admin/fees/merchant/${mid}?service_type=payout`);
      toast.success('Override removed');
      await load();
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const g = config?.payout_global;

  return (
    <div className="space-y-6" data-testid="fee-management-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Platform Fee Management</h1>
        <p className="text-muted-foreground">Configure transaction fees, fixed charges and applicable taxes for merchant services.</p>
      </div>

      {/* Revenue cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {revenue && [
          ['Today', revenue.today], ['Last 7 Days', revenue.last_7_days],
          ['Last 30 Days', revenue.last_30_days], ['All Time', revenue.all_time],
        ].map(([label, w]) => (
          <Card key={label} data-testid={`revenue-${label.replace(/\s/g, '-').toLowerCase()}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><TrendingUp className="w-3.5 h-3.5" />{label}</div>
              <p className="text-2xl font-bold font-mono">{inr(w.total)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Fee {inr(w.platform_fee)} · Tax {inr(w.tax)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Global fee config */}
        <Card data-testid="global-fee-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-heading">Payout Platform Fee</CardTitle>
                <CardDescription>Global fee applied to all merchants unless overridden.</CardDescription>
              </div>
              <Badge variant={g?.enabled ? 'default' : 'secondary'} data-testid="global-fee-status">{g?.enabled ? 'Active' : 'Disabled'}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Percentage Fee (%)</Label>
                <div className="relative">
                  <Percent className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input className="pl-9" type="number" step="0.01" value={form.percentage_fee} onChange={(e) => setForm({ ...form, percentage_fee: e.target.value })} data-testid="global-percentage-input" />
                </div>
              </div>
              <div>
                <Label>Fixed Fee (₹)</Label>
                <div className="relative">
                  <IndianRupee className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input className="pl-9" type="number" step="0.01" value={form.fixed_fee} onChange={(e) => setForm({ ...form, fixed_fee: e.target.value })} data-testid="global-fixed-input" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="cursor-pointer">Enable Tax / GST</Label>
                <p className="text-xs text-muted-foreground">Tax is charged on the platform fee.</p>
              </div>
              <Switch checked={form.tax_enabled} onCheckedChange={(v) => setForm({ ...form, tax_enabled: v })} data-testid="global-tax-toggle" />
            </div>
            {form.tax_enabled && (
              <div>
                <Label>Tax Rate (%)</Label>
                <Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="global-tax-input" />
              </div>
            )}
            <div>
              <Label>Effective From (optional)</Label>
              <Input type="datetime-local" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="global-effective-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Leave empty to apply immediately. Existing payouts keep their original fee.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label className="cursor-pointer">Enable Fee</Label>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="global-enable-toggle" />
            </div>
            <Button onClick={saveGlobal} disabled={saving} className="w-full" data-testid="save-global-fee-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Fee Configuration
            </Button>
          </CardContent>
        </Card>

        {/* Preview calculator + Payin inactive */}
        <div className="space-y-6">
          <Card data-testid="fee-calculator-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2"><Calculator className="w-5 h-5" />Test Transaction</CardTitle>
              <CardDescription>Verify the configured fee before saving.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Payout Amount (₹)</Label>
                <Input type="number" value={previewAmt} onChange={(e) => setPreviewAmt(e.target.value)} data-testid="preview-amount-input" />
              </div>
              {preview && (
                <div className="rounded-lg border divide-y text-sm" data-testid="preview-result">
                  {[
                    ['Payout Amount', preview.amount],
                    ['Percentage Fee', preview.percentage_fee_amount],
                    ['Fixed Fee', preview.fixed_fee],
                    ['Platform Fee', preview.platform_fee],
                    ['Tax', preview.tax_amount],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between px-4 py-2"><span className="text-muted-foreground">{l}</span><span className="font-mono">{inr(v)}</span></div>
                  ))}
                  <div className="flex justify-between px-4 py-3 bg-muted/40"><span className="font-semibold">Total Debit</span><span className="font-mono font-bold text-primary" data-testid="preview-total">{inr(preview.total_debit)}</span></div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="opacity-70" data-testid="payin-fee-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading">Payin Platform Fee</CardTitle>
                <Badge variant="secondary">Inactive</Badge>
              </div>
              <CardDescription>Payin is operationally inactive. Fee configuration will be enabled when Payin launches.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>

      {/* Merchant overrides */}
      <Card data-testid="merchant-overrides-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><Users className="w-5 h-5" />Merchant-Specific Overrides</CardTitle>
          <CardDescription>Override the global fee for individual merchants. Overrides always win over the global fee.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>Merchant</Label>
              <Select value={ovMerchant} onValueChange={setOvMerchant}>
                <SelectTrigger data-testid="override-merchant-select"><SelectValue placeholder="Select merchant" /></SelectTrigger>
                <SelectContent>
                  {merchants.map((m) => (<SelectItem key={m.id} value={m.id}>{m.business_name || m.id}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>%</Label><Input type="number" step="0.01" value={ovForm.percentage_fee} onChange={(e) => setOvForm({ ...ovForm, percentage_fee: e.target.value })} data-testid="override-pct-input" /></div>
            <div><Label>Fixed ₹</Label><Input type="number" step="0.01" value={ovForm.fixed_fee} onChange={(e) => setOvForm({ ...ovForm, fixed_fee: e.target.value })} data-testid="override-fixed-input" /></div>
            <div className="flex gap-2">
              <div className="flex-1"><Label>Tax %</Label><Input type="number" step="0.01" disabled={!ovForm.tax_enabled} value={ovForm.tax_rate} onChange={(e) => setOvForm({ ...ovForm, tax_rate: e.target.value })} /></div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={ovForm.tax_enabled} onCheckedChange={(v) => setOvForm({ ...ovForm, tax_enabled: v })} data-testid="override-tax-toggle" />Enable tax</label>
            <Button onClick={saveOverride} disabled={ovSaving} data-testid="save-override-btn">
              {ovSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Override
            </Button>
          </div>

          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr><th className="px-4 py-2">Merchant</th><th className="px-4 py-2">Percentage</th><th className="px-4 py-2">Fixed</th><th className="px-4 py-2">Tax</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody>
                {(config?.merchant_overrides || []).length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No merchant overrides. All merchants use the global fee.</td></tr>
                )}
                {(config?.merchant_overrides || []).map((o) => (
                  <tr key={o.id} className="border-t" data-testid={`override-row-${o.merchant_id}`}>
                    <td className="px-4 py-2">{o.merchant?.business_name || o.merchant_id}</td>
                    <td className="px-4 py-2 font-mono">{o.percentage_fee}%</td>
                    <td className="px-4 py-2 font-mono">{inr(o.fixed_fee)}</td>
                    <td className="px-4 py-2 font-mono">{o.tax_enabled ? `${o.tax_rate}%` : '—'}</td>
                    <td className="px-4 py-2"><Badge variant={o.enabled ? 'default' : 'secondary'}>{o.enabled ? 'Active' : 'Disabled'}</Badge></td>
                    <td className="px-4 py-2 text-right">
                      <Button size="icon" variant="ghost" onClick={() => removeOverride(o.merchant_id)} data-testid={`remove-override-${o.merchant_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
